import React, { useState } from 'react';
import Button from '../Button';
import { rescheduleFamilyPortalLesson } from '../../data/familyPortal';
import {
  CANCEL_SIDES, familyPortalCancelNoteMax, familyPortalRefusal, familyPortalWord as w,
} from '../../domain/familyPortal';
import { formatLongDate, formatTime } from '../../domain/locale';
import { zoneAbbreviation } from '../../domain/timeZone';
import { usePortalSlots } from '../../hooks/usePortalSlots';
import p from '../../pages/PortalPage.module.css';
import b from './FindAndBook.module.css';
import c from './CancelBooking.module.css';
import s from './Reschedule.module.css';

// Reschedule (#543): move one lesson of one student to an open time, in one
// step. MMS offers Reschedule on cancelling where the policy issues make-up
// credits — the cancel, then a new time chosen with that credit, in one step —
// which is unchecked live (#543 §2). Ours is Cancel Booking's dialog
// (CancelBooking.jsx) with a time picker from Find & Book's slots in it:
//
//   * the lesson, the studio's Policy Text, the side of the deadline and
//     what moving it does to it — the cancel's sentence, never a credit
//     issued (functions/portal/reschedule.js);
//   * the times the student may move it to (`familyPortalSlots` asked for
//     this lesson), each a make-up lesson; choosing one says what it costs
//     in credit, and — where the credit does not cover it — that it still
//     goes ahead, the balance it leaves and what a shortfall is charged at;
//   * a Note to Teacher, and Reschedule.
//
// Yes sends back the code of the time chosen; the server refuses
// (`reschedule_changed`) when moving there no longer does what was shown.
// Every time is the server's, on the family's clock. No `Date` is built.

// Refusals that mean the lesson itself has changed: back to a fresh page.
const LESSON_STALE = ['not_on_lesson', 'cancel_started', 'cancel_recorded', 'reschedule_off', 'no_student', 'cancel_paid'];
// Refusals that mean the times shown are out of date: ask for them again.
const TIMES_STALE = [
  'reschedule_changed', 'slot_full', 'slot_gone', 'book_too_soon', 'book_too_far', 'already_booked', 'reschedule_shared',
  'book_conflict',
];

// Why the student cannot move to a time, beside it.
const NOT_ELIGIBLE = {
  already_booked: 'notEligibleBooked',
  book_conflict: 'notEligibleConflict',
  reschedule_shared: 'notEligibleShared',
};
const whyNot = (reason) => (NOT_ELIGIBLE[reason] ? w(NOT_ELIGIBLE[reason]) : w('rescheduleNotEligible'));

export default function Reschedule({
  view, choice, lesson, studentId, onClose, onRescheduled,
}) {
  const offer = lesson.reschedule?.[studentId] || null;
  const { answer, error, loading, retry } = usePortalSlots(offer ? choice : null, { studentId, occurrenceId: lesson.id });
  const [chosenId, setChosenId] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [done, setDone] = useState(null);
  const locale = view.studio.locale || undefined;
  const zone = view.family.timeZone;
  const who = view.students.find((x) => x.id === studentId);
  const max = familyPortalCancelNoteMax();

  const clock = (date, time) => {
    if (!time) return null;
    const label = zone ? zoneAbbreviation(zone, date, time, locale || 'en-US') : '';
    return `${formatTime(time, locale)}${label ? ` ${label}` : ''}`;
  };
  const span = (x) => (x.startTime && x.endTime
    ? `${formatTime(x.startTime, locale)} – ${clock(x.endDate || x.date, x.endTime)}`
    : clock(x.date, x.startTime));

  const factsOf = (x) => (
    <div className={b.facts}>
      <span className={b.fact}>{who?.name || who?.firstName || ''}</span>
      <span className={b.fact}>{formatLongDate(x.date, locale)}</span>
      {x.startTime ? <span className={b.fact}>{clock(x.date, x.startTime)}</span> : null}
      {x.category ? <span className={b.fact}>{x.category}</span> : null}
    </div>
  );

  if (done) {
    return (
      <section className={p.panel}>
        <h2 className={b.complete}>{w('rescheduleDone')}</h2>
        <p className={s.heading}>{w('rescheduleFrom')}</p>
        {factsOf(lesson)}
        <p className={s.heading}>{w('rescheduleTo')}</p>
        {factsOf({ ...done.slot, ...(done.result?.slot || {}) })}
        <div className={b.actions}><Button onClick={onRescheduled}>{w('done')}</Button></div>
      </section>
    );
  }

  if (!offer) {
    return (
      <section className={p.panel}>
        <p className={p.body}>{familyPortalRefusal('cancel_unavailable')}</p>
        <div className={b.actions}><Button variant="secondary" onClick={onClose}>{w('cancel')}</Button></div>
      </section>
    );
  }

  const slots = answer?.slots || [];
  const chosen = chosenId ? slots.find((x) => x.id === chosenId) || null : null;
  const judged = chosen ? chosen.students.find((j) => j.id === studentId) || null : null;

  const yes = () => {
    if (busy || !chosen || !judged?.ok) return;
    setBusy(true);
    setFailure(null);
    rescheduleFamilyPortalLesson({
      studioUid: choice.studioUid,
      familyId: choice.familyId,
      studentId,
      occurrenceId: lesson.id,
      toOccurrenceId: chosen.id,
      expected: judged.code,
      note: note.trim() || null,
    }).then(
      (result) => { setBusy(false); setDone({ slot: chosen, result }); },
      (err) => {
        setBusy(false);
        setFailure({ message: err?.message || familyPortalRefusal('failed'), refusal: err?.refusal || null });
      },
    );
  };

  const lessonStale = LESSON_STALE.includes(failure?.refusal);
  const timesStale = TIMES_STALE.includes(failure?.refusal);
  const used = [...note].length;

  let times;
  if (error) times = <p className={p.error} role="alert">{error.message}</p>;
  else if (loading || !answer) times = <p className={p.body}>{w('loading')}</p>;
  else if (!slots.length) times = <p className={p.body}>{w('rescheduleNoSlots')}</p>;
  else {
    times = (
      <ul className={s.times}>
        {slots.map((x) => {
          const j = x.students.find((y) => y.id === studentId);
          const ok = Boolean(j?.ok);
          let cls = s.time;
          if (!ok) cls = s.timeOff;
          else if (x.id === chosenId) cls = s.timeChosen;
          return (
            <li key={x.id}>
              <button
                type="button"
                className={cls}
                aria-pressed={x.id === chosenId}
                disabled={!ok || busy}
                onClick={() => setChosenId(x.id)}
              >
                <span className={s.timeWhen}>{`${formatLongDate(x.date, locale)}, ${span(x)}`}</span>
                <span className={s.timeMore}>{[x.category, ok ? null : whyNot(j?.reason)].filter(Boolean).join(' · ')}</span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section className={p.panel}>
      <h2 className={p.panelTitle}>{w('rescheduleTitle')}</h2>
      <p className={s.heading}>{w('rescheduleFrom')}</p>
      {factsOf(lesson)}
      {view.studio.policyText ? (
        <div className={c.policy}>
          <span className={c.policyTitle}>{w('cancelPolicy')}</span>
          {view.studio.policyText}
        </div>
      ) : null}
      <p className={c.effect}>
        {offer.side === CANCEL_SIDES.before ? w('cancelBeforeDeadline') : w('cancelAfterDeadline')}
        {offer.deadline ? ` ${w('cancelDeadline')}: ${formatLongDate(offer.deadline.date, locale)} ${clock(offer.deadline.date, offer.deadline.time) || ''}.` : ''}
      </p>
      <p className={c.effect}>{offer.text}</p>

      <p className={s.heading}>{w('rescheduleChoose')}</p>
      {times}
      {judged?.ok && judged.text ? <p className={c.effect}>{judged.text}</p> : null}

      {chosen ? (
        <>
          <p className={c.confirm}>{w('rescheduleConfirm')}</p>
          <div className={p.form}>
            <label className={p.label} htmlFor="portal-reschedule-note">{w('cancelNote')}</label>
            <textarea
              id="portal-reschedule-note"
              className={c.note}
              value={note}
              maxLength={max}
              disabled={busy}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className={c.noteFoot}>
              <span>{w('cancelNoteOptional')}</span>
              <span>{`${used} / ${max}`}</span>
            </div>
          </div>
        </>
      ) : null}
      {failure ? <p className={p.error} role="alert">{failure.message}</p> : null}
      <div className={b.actions}>
        {lessonStale ? <Button variant="secondary" onClick={onRescheduled}>{w('retry')}</Button> : null}
        {timesStale ? (
          <Button variant="secondary" onClick={() => { setFailure(null); setChosenId(null); retry(); }}>{w('rescheduleOtherTime')}</Button>
        ) : null}
        {lessonStale || timesStale ? null : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>{w('cancel')}</Button>
            <Button onClick={yes} loading={busy} disabled={!judged?.ok}>{busy ? w('rescheduling') : w('rescheduleYes')}</Button>
          </>
        )}
      </div>
    </section>
  );
}
